import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, Upload, Download, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAX_STATUSES = [
  { value: 'individual', label: 'Individual / Sole Proprietor' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'llc', label: 'LLC' },
  { value: 's_corp', label: 'S-Corp' },
  { value: 'c_corp', label: 'C-Corp' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
];

/**
 * VIP Tax / 1099 / W-9 admin section.
 * Renders inside the AdminVips edit dialog. Admin-only.
 *
 * SSN / EIN are encrypted server-side (Fernet) and never returned in plaintext.
 * The form only shows last 4 digits + boolean "on file" indicators.
 */
const VipTaxSection = ({ vipId }) => {
  const [tax, setTax] = useState(null);
  const [form, setForm] = useState({ tax_status: '', legal_name: '', ssn: '', ein: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (vipId) refresh(); }, [vipId]);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/vips/${vipId}/tax`, { withCredentials: true });
      setTax(data);
      setForm({
        tax_status: data.tax_status || '',
        legal_name: data.legal_name || '',
        ssn: '',
        ein: '',
        notes: data.notes || '',
      });
    } catch (err) {
      // ignore
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tax_status: form.tax_status || null,
        legal_name: form.legal_name,
        notes: form.notes,
      };
      if (form.ssn.trim()) payload.ssn = form.ssn.trim();
      if (form.ein.trim()) payload.ein = form.ein.trim();
      await axios.put(`${API}/admin/vips/${vipId}/tax`, payload, { withCredentials: true });
      toast.success('Tax info saved');
      // Clear secret inputs on success
      setForm((f) => ({ ...f, ssn: '', ein: '' }));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const uploadW9 = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/vips/${vipId}/tax/w9`, fd, { withCredentials: true });
      toast.success('W-9 uploaded (encrypted at rest)');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const downloadW9 = async () => {
    try {
      const res = await axios.get(`${API}/admin/vips/${vipId}/tax/w9`, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = tax?.w9_file_name || 'w9.pdf';
      document.body.appendChild(a);
      a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (err) {
      toast.error('Could not download W-9');
    }
  };

  const deleteW9 = async () => {
    if (!window.confirm('Remove the W-9 on file for this VIP? The encrypted PDF will be deleted.')) return;
    try {
      await axios.delete(`${API}/admin/vips/${vipId}/tax/w9`, { withCredentials: true });
      toast.success('W-9 removed');
      refresh();
    } catch (err) {
      toast.error('Failed');
    }
  };

  if (!tax) return null;

  return (
    <section data-testid="vip-tax-section" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Tax / 1099 / W-9
        </h4>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <Badge className={tax.w9_received ? 'bg-green-900/30 text-green-400 border-green-700' : 'bg-red-900/30 text-red-400 border-red-700'}>
            {tax.w9_received ? 'W-9 ON FILE' : 'W-9 MISSING'}
          </Badge>
          {tax.needs_1099 ? (
            <Badge className="bg-amber-900/30 text-amber-400 border-amber-700">
              1099 REQUIRED · ${tax.current_year_paid.toLocaleString()}
            </Badge>
          ) : (
            <Badge className="bg-[#262626] text-[#A3A3A3] border-[#262626]">
              Under $600 · ${tax.current_year_paid.toLocaleString()}
            </Badge>
          )}
        </div>
      </div>

      {/* Year-to-date stats */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-2.5">
          <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Current year paid</p>
          <p className="text-white font-bold text-lg mt-0.5">${tax.current_year_paid.toLocaleString()}</p>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-md p-2.5">
          <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Lifetime paid</p>
          <p className="text-white font-bold text-lg mt-0.5">${tax.lifetime_paid.toLocaleString()}</p>
        </div>
      </div>

      {/* W-9 file ops */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-3 flex flex-wrap items-center gap-2">
        <FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          {tax.w9_file_name ? (
            <p className="text-white truncate">
              {tax.w9_file_name}
              <span className="text-[#A3A3A3] text-xs ml-2">uploaded {tax.w9_received_at ? new Date(tax.w9_received_at).toLocaleDateString() : '—'}</span>
            </p>
          ) : (
            <p className="text-[#A3A3A3]">No W-9 on file. Upload a PDF or scan.</p>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          data-testid="vip-w9-file-input"
          onChange={(e) => uploadW9(e.target.files?.[0])}
          className="hidden"
        />
        <Button
          data-testid="vip-w9-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          size="sm"
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? 'Uploading…' : 'Upload W-9'}
        </Button>
        {tax.w9_file_id && (
          <>
            <Button data-testid="vip-w9-download-btn" onClick={downloadW9} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
            <Button data-testid="vip-w9-delete-btn" onClick={deleteW9} size="sm" variant="outline" className="border-red-700/40 text-red-400 hover:bg-red-900/20">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-white text-xs">Tax classification</Label>
          <Select value={form.tax_status} onValueChange={(v) => setForm({ ...form, tax_status: v })}>
            <SelectTrigger data-testid="vip-tax-status-select" className="bg-[#0A0A0A] border-[#262626] text-white h-11">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              {TAX_STATUSES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white text-xs">Legal name (per W-9)</Label>
          <Input
            data-testid="vip-tax-legal-name"
            value={form.legal_name}
            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white h-11"
          />
        </div>
        <div>
          <Label className="text-white text-xs flex items-center gap-2">
            SSN
            {tax.ssn_on_file && <Badge className="bg-green-900/30 text-green-400 border-green-700 text-[9px]">ON FILE</Badge>}
            {tax.ssn_last4 && <span className="text-[#A3A3A3] text-[10px]">···{tax.ssn_last4}</span>}
          </Label>
          <Input
            data-testid="vip-tax-ssn"
            type="password"
            placeholder={tax.ssn_on_file ? 'Leave blank to keep stored value' : '9 digits — encrypted at rest'}
            value={form.ssn}
            onChange={(e) => setForm({ ...form, ssn: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white h-11 font-mono"
          />
        </div>
        <div>
          <Label className="text-white text-xs flex items-center gap-2">
            EIN
            {tax.ein_on_file && <Badge className="bg-green-900/30 text-green-400 border-green-700 text-[9px]">ON FILE</Badge>}
          </Label>
          <Input
            data-testid="vip-tax-ein"
            type="password"
            placeholder={tax.ein_on_file ? 'Leave blank to keep stored value' : '9 digits — encrypted at rest'}
            value={form.ein}
            onChange={(e) => setForm({ ...form, ein: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white h-11 font-mono"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-white text-xs">Notes</Label>
          <Textarea
            data-testid="vip-tax-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="vip-tax-save"
          onClick={save}
          disabled={saving}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
          size="sm"
        >
          {saving ? 'Saving…' : 'Save tax info'}
        </Button>
      </div>

      {!tax.w9_received && tax.needs_1099 && (
        <div className="flex items-center gap-2 text-amber-400 text-xs bg-amber-900/10 border border-amber-700/40 rounded p-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          This VIP crossed the $600 threshold this year but no W-9 is on file. Collect one before issuing a 1099.
        </div>
      )}
    </section>
  );
};

/**
 * Compact acceptance history list for the VIP edit dialog.
 */
export const VipAcceptanceHistory = ({ vipId }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!vipId) return;
    axios.get(`${API}/admin/vips/${vipId}/terms-acceptances`, { withCredentials: true })
      .then(({ data }) => setRows(data.rows || []))
      .catch(() => setRows([]));
  }, [vipId]);

  if (!rows.length) {
    return (
      <section data-testid="vip-acceptance-history" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4">
        <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Terms Acceptance History</h4>
        <p className="text-[#A3A3A3] text-sm">No acceptances recorded yet.</p>
      </section>
    );
  }

  return (
    <section data-testid="vip-acceptance-history" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4">
      <h4 className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Terms Acceptance History</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-[#262626]">
            <tr>
              <th className="text-left py-2 text-[#A3A3A3]">Version</th>
              <th className="text-left py-2 text-[#A3A3A3]">Accepted</th>
              <th className="text-left py-2 text-[#A3A3A3]">IP</th>
              <th className="text-left py-2 text-[#A3A3A3]">Device</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#262626] last:border-0">
                <td className="py-2 text-[#D4AF37] font-mono">v{r.version}</td>
                <td className="py-2 text-white">{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : '—'}</td>
                <td className="py-2 text-[#A3A3A3] font-mono">{r.ip_address || '—'}</td>
                <td className="py-2 text-[#A3A3A3] truncate max-w-[280px]" title={r.user_agent}>{r.user_agent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default VipTaxSection;
